import moment from 'moment';
import { AccountData, OrderData, VoucherOut, VoucherType, WaitingListItemData } from '../../../../api';
import { DATE_FORMAT } from '../../../calendarWrapper/CalendarWrapper';
import { holdFieldsFromExpiry } from '../../../../common/pendingOrderUtils';
import { EditOrderFormValues } from '../EditOrderTypes';
import { NINE_AM } from './orderFormDefaults';

type OrderFormInitialValuesArgs = {
  editOrderState: any;
  order: Partial<OrderData>;
  isEditOrder: boolean;
  isEventOrCourse: boolean;
  voucherToEdit?: VoucherOut;
  voucherToRedeem?: VoucherOut;
  waitingListItem?: WaitingListItemData;
  account: AccountData;
  isPendingEditedOrder: boolean;
  // Replaces the date that would otherwise come from the waiting list item or the order (clone passes '').
  dateOverride?: string;
};

const getDeliveryDate = (voucherToEdit: VoucherOut | undefined, orderAny: any, account: AccountData) => {
  if (voucherToEdit?.delivery_timestamp) {
    return moment.utc(voucherToEdit.delivery_timestamp).tz(account.timezone).format(DATE_FORMAT);
  }
  if (orderAny?.delivery_timestamp) {
    return moment
      .utc(orderAny.delivery_timestamp as string)
      .tz(account.timezone)
      .format(DATE_FORMAT);
  }
  return moment().format(DATE_FORMAT);
};

const getDeliveryTime = (voucherToEdit: VoucherOut | undefined, orderAny: any, account: AccountData) => {
  const ts = voucherToEdit?.delivery_timestamp || (orderAny?.delivery_timestamp as string | undefined);
  if (ts) {
    return moment.utc(ts).tz(account.timezone).format('HH:mm');
  }
  return '15:00';
};

const getDefaultExpirationDate = (voucherToEdit: VoucherOut | undefined, editOrderState: any, account: AccountData) => {
  if (!voucherToEdit?.created_at) {
    return '';
  }
  if (voucherToEdit.type === VoucherType.MONETARY) {
    const validityYears = account.monetary_voucher_settings?.validity_years;
    return validityYears ? moment(voucherToEdit.created_at).add(validityYears, 'years').format(DATE_FORMAT) : '';
  }
  const maxValidDays = editOrderState.orderPage.effective_vouchers_config?.max_valid_days;
  return maxValidDays ? moment(voucherToEdit.created_at).add(maxValidDays, 'days').format(DATE_FORMAT) : '';
};

export const buildOrderFormInitialValues = ({
  editOrderState,
  order,
  isEditOrder,
  isEventOrCourse,
  voucherToEdit,
  voucherToRedeem,
  waitingListItem,
  account,
  isPendingEditedOrder,
  dateOverride,
}: OrderFormInitialValuesArgs): EditOrderFormValues => {
  const orderAny = order as any;
  const orderInitialDuration = order.duration || 0;

  return {
    firstname: editOrderState.personalDetails?.firstname || '',
    lastname: editOrderState.personalDetails?.lastname || '',
    alternative_full_name: editOrderState.personalDetails?.alternative_full_name || '',
    email: editOrderState.personalDetails?.email || '',
    phone: editOrderState.personalDetails?.phone || '',
    custom_personal_details: order.custom_personal_details || {},
    referral_source: order.referral_source,
    voucher_greeting_message: voucherToEdit?.voucher_greeting_message || orderAny?.voucher_greeting_message || '',
    should_send_to_recipient: !!(voucherToEdit?.recipient_email || orderAny?.recipient_email),
    recipient_email: voucherToEdit?.recipient_email || orderAny?.recipient_email || '',
    delivery_date:
      voucherToEdit?.delivery_timestamp || orderAny?.delivery_timestamp
        ? getDeliveryDate(voucherToEdit, orderAny, account)
        : null,
    delivery_time: getDeliveryTime(voucherToEdit, orderAny, account),
    should_send_delayed: !!(voucherToEdit?.delivery_timestamp || orderAny?.delivery_timestamp),

    date: dateOverride ?? (waitingListItem?.date || (order.date as string) || moment().format(DATE_FORMAT)),
    timeSeconds: order.time ?? NINE_AM,
    privateOrderEndTimeSeconds:
      isEditOrder && order.time && !isEventOrCourse
        ? moment.unix(order.time + orderInitialDuration).unix()
        : editOrderState.orderPage.duration + (order.time ?? NINE_AM),

    selectedUsersIds: order.employee_ids || [],
    selectedHolds: isEditOrder ? order.manual_resource_holds ?? [] : [],
    skipResourceAssignment: order.skip_resource_assignment || false,
    resourceOptionId: order.resource_option_id || '',
    comment: order.comment || voucherToEdit?.comment || voucherToRedeem?.comment || '',
    shouldUnarchive: false,
    expirationDate: voucherToEdit?.expiration_date || getDefaultExpirationDate(voucherToEdit, editOrderState, account),
    skipCustomerEditNotifications: false,

    createPaymentLink: false,
    upfrontAmount: 0,
    upfrontEdited: false,
    ...(() => {
      const { holdDate, holdTime } = isPendingEditedOrder
        ? holdFieldsFromExpiry(order.pending_expires_at, account.timezone)
        : { holdDate: '', holdTime: '' };
      return {
        paymentLinkHoldDate: holdDate,
        paymentLinkHoldTime: holdTime,
        paymentLinkHoldQuickKey: holdDate ? '' : 'noLimit',
      };
    })(),
  };
};
